import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import withModal from "./withModal";
import CommonButton from "./commonButton";

const MapPickerModalInner = ({ onClose, latitude, longitude, onSave }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCoords, setSelectedCoords] = useState({
    lat: latitude ? parseFloat(latitude) : 20.5937, // default to India
    lng: longitude ? parseFloat(longitude) : 78.9629,
  });
  const [isLeafletLoaded, setIsLeafletLoaded] = useState(false);

  // Load Leaflet scripts dynamically from CDN to avoid bundler issues with Leaflet assets
  useEffect(() => {
    if (window.L) {
      setIsLeafletLoaded(true);
      return;
    }

    // Load stylesheet
    let link = document.querySelector('link[href*="leaflet.css"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    // Load script
    let script = document.querySelector('script[src*="leaflet.js"]');
    if (!script) {
      script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.onload = () => setIsLeafletLoaded(true);
      document.head.appendChild(script);
    } else {
      const interval = setInterval(() => {
        if (window.L) {
          clearInterval(interval);
          setIsLeafletLoaded(true);
        }
      }, 100);
    }
  }, []);

  // Reset search query and sync coords on mount/change
  useEffect(() => {
    setSearchQuery("");
    const initialLat = latitude ? parseFloat(latitude) : 20.5937;
    const initialLng = longitude ? parseFloat(longitude) : 78.9629;
    setSelectedCoords({ lat: initialLat, lng: initialLng });
  }, [latitude, longitude]);

  // Initialize Map
  useEffect(() => {
    if (!isLeafletLoaded || !mapContainerRef.current) return;

    // Reset previous instance if it exists
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerInstanceRef.current = null;
    }

    const L = window.L;

    const initialLat = latitude ? parseFloat(latitude) : 20.5937;
    const initialLng = longitude ? parseFloat(longitude) : 78.9629;
    const zoomLevel = latitude && longitude ? 15 : 5;

    setSelectedCoords({ lat: initialLat, lng: initialLng });

    // Create Map
    const map = L.map(mapContainerRef.current).setView([initialLat, initialLng], zoomLevel);
    mapInstanceRef.current = map;

    // Add OS TileLayer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Create Marker
    const marker = L.marker([initialLat, initialLng], {
      draggable: true,
    }).addTo(map);
    markerInstanceRef.current = marker;

    // Listen to dragend
    marker.on("dragend", () => {
      const position = marker.getLatLng();
      setSelectedCoords({ lat: position.lat, lng: position.lng });
    });

    // Listen to map click to position marker
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      setSelectedCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    // Solve Leaflet rendering inside Bootstrap modals
    setTimeout(() => {
      map.invalidateSize();
    }, 200);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerInstanceRef.current = null;
      }
    };
  }, [isLeafletLoaded, latitude, longitude]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newLat = parseFloat(lat);
        const newLng = parseFloat(lon);

        setSelectedCoords({ lat: newLat, lng: newLng });

        if (mapInstanceRef.current && markerInstanceRef.current) {
          mapInstanceRef.current.setView([newLat, newLng], 15);
          markerInstanceRef.current.setLatLng([newLat, newLng]);
        }
      } else {
        alert("Location not found. Please try a different search.");
      }
    } catch (err) {
      console.error("Geocoding search error:", err);
    }
  };

  const handleSave = async () => {
    let address = "";
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${selectedCoords.lat}&lon=${selectedCoords.lng}`
      );
      const data = await response.json();
      if (data && data.display_name) {
        address = data.display_name;
      }
    } catch (err) {
      console.error("Reverse geocoding error:", err);
    }
    onSave(selectedCoords.lat.toFixed(6), selectedCoords.lng.toFixed(6), address);
    onClose();
  };

  return (
    <>
      <Modal.Body>
        <Form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
          <Form.Control
            type="text"
            placeholder="Search address, city, district or landmark..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <CommonButton type="submit" variant="primary" icon={<Search size={16} />} title="Search" />
        </Form>
        <div
          ref={mapContainerRef}
          className="w-100 ronded-3 border"
          style={{ height: "400px"}}
        />
        <div className="mt-2 text-muted small">
          Coordinates: {selectedCoords.lat.toFixed(6)}, {selectedCoords.lng.toFixed(6)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton variant="outline-secondary" onClick={onClose} title="Cancel" />
        <CommonButton variant="primary" onClick={handleSave} title="Confirm Location" />
      </Modal.Footer>
    </>
  );
};

const MapPickerModal = withModal(MapPickerModalInner);

export default MapPickerModal;
